import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  PencilIcon, 
  TrashIcon, 
  ArrowPathIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface Expense {
  id: number;
  category: string;
  subcategory: string;
  amount: number;
  date: string;
  description: string;
  receipts?: string;
  createdAt: string;
  updatedAt: string;
}

interface CategoryTotal {
  category: string;
  total: number;
}

const ExpenseList: React.FC = () => {
  const { can } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<{ from: string; to: string }>({
    from: '',
    to: ''
  });
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  
  useEffect(() => {
    fetchExpenses();
  }, []);
  
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/expenses');
      setExpenses(response.data);
      
      // Extract unique categories
      const uniqueCategories = Array.from(
        new Set(response.data.map((expense: Expense) => expense.category))
      ) as string[];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setError('Ошибка при загрузке расходов');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle expense deletion
  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту запись о расходе?')) {
      try {
        await axios.delete(`/expenses/${id}`);
        setExpenses(expenses.filter(expense => expense.id !== id));
      } catch (error) {
        console.error('Error deleting expense:', error);
        alert('Ошибка при удалении расхода');
      }
    }
  };
  
  // Filter expenses by search term, date range, and category
  const filteredExpenses = expenses.filter(expense => {
    // Category filter
    if (categoryFilter !== 'all' && expense.category !== categoryFilter) {
      return false;
    }
    
    // Date filter
    if (dateFilter.from && dateFilter.to) {
      const expenseDate = new Date(expense.date);
      const fromDate = new Date(dateFilter.from);
      const toDate = new Date(dateFilter.to);
      
      // Adjust toDate to the end of the day
      toDate.setHours(23, 59, 59, 999);
      
      if (expenseDate < fromDate || expenseDate > toDate) {
        return false;
      }
    } else if (dateFilter.from) {
      const expenseDate = new Date(expense.date);
      const fromDate = new Date(dateFilter.from);
      
      if (expenseDate < fromDate) {
        return false;
      }
    } else if (dateFilter.to) {
      const expenseDate = new Date(expense.date);
      const toDate = new Date(dateFilter.to);
      toDate.setHours(23, 59, 59, 999);
      
      if (expenseDate > toDate) {
        return false;
      }
    }
    
    // Search filter
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      expense.category.toLowerCase().includes(searchLower) ||
      expense.subcategory.toLowerCase().includes(searchLower) ||
      expense.description.toLowerCase().includes(searchLower) ||
      String(expense.amount).includes(searchTerm)
    );
  });
  
  // Calculate category totals for the filtered expenses
  const getCategoryTotals = (): CategoryTotal[] => {
    const totals: Record<string, number> = {};
    
    filteredExpenses.forEach(expense => {
      if (!totals[expense.category]) {
        totals[expense.category] = 0;
      }
      totals[expense.category] += expense.amount;
    });
    
    return Object.entries(totals)
      .map(([category, total]) => ({ category, total }))
      .sort((a, b) => b.total - a.total);
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Расходы"
        subtitle="Управление и отслеживание расходов компании"
        actions={
          can('expenses.create') ? (
            <Link
              to="/expenses/new"
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Добавить расход
            </Link>
          ) : null
        }
      />
      
      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}
      
      {/* Filters */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end space-y-4 md:space-y-0 md:space-x-4">
        {/* Search box */}
        <div className="w-full md:w-64">
          <label htmlFor="search" className="form-label">Поиск</label>
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              id="search"
              type="text"
              placeholder="Категория, описание..."
              className="form-input pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        {/* Category filter */}
        <div className="w-full md:w-48">
          <label htmlFor="category-filter" className="form-label">Категория</label>
          <select
            id="category-filter"
            className="form-input"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">Все категории</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
        
        {/* Date range filter */}
        <div className="w-full md:w-36">
          <label htmlFor="date-from" className="form-label">Дата (от)</label>
          <input
            id="date-from"
            type="date"
            className="form-input"
            value={dateFilter.from}
            onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })}
          />
        </div>
        
        <div className="w-full md:w-36">
          <label htmlFor="date-to" className="form-label">Дата (до)</label>
          <input
            id="date-to"
            type="date"
            className="form-input"
            value={dateFilter.to}
            onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })}
          />
        </div>
        
        {/* Refresh button */}
        <button
          type="button"
          onClick={fetchExpenses}
          className="btn-outline flex items-center"
        >
          <ArrowPathIcon className="h-5 w-5 mr-1" />
          Обновить
        </button>
      </div>
      
      {/* Expenses table */}
      {filteredExpenses.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">
            {searchTerm || dateFilter.from || dateFilter.to || categoryFilter !== 'all'
              ? 'Расходы не найдены' 
              : 'Нет расходов'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дата
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Категория
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Подкатегория
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Описание
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Сумма
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredExpenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    {/* Removed whitespace-nowrap */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatDate(expense.date)}
                      </div>
                    </td>
                    {/* Removed whitespace-nowrap, added break-words */}
                    <td className="px-6 py-4 break-words">
                      <div className="text-sm text-gray-900">
                        {expense.category}
                      </div>
                    </td>
                    {/* Removed whitespace-nowrap, added break-words */}
                    <td className="px-6 py-4 break-words">
                      <div className="text-sm text-gray-900">
                        {expense.subcategory}
                      </div>
                    </td>
                    {/* Added break-words */}
                    <td className="px-6 py-4 break-words">
                      <div className="text-sm text-gray-900 max-w-md">
                        {expense.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-red-600">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        {can('expenses.edit') && (
                          <Link
                            to={`/expenses/edit/${expense.id}`}
                            className="text-gray-600 hover:text-gray-900"
                            title="Редактировать"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </Link>
                        )}
                        {can('expenses.delete') && (
                          <button
                            onClick={() => handleDelete(expense.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Удалить"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-right font-medium text-gray-700">
                    Итого:
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-red-600">
                    {formatCurrency(filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
      
      {/* Category summary */}
      {filteredExpenses.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Расходы по категориям</h3>
            <Link 
              to="/reports/expenses" 
              className="text-sm text-primary hover:text-primary-dark flex items-center"
            >
              <ChartBarIcon className="h-4 w-4 mr-1" />
              Подробный отчет
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getCategoryTotals().map(item => (
              <div key={item.category} className="bg-gray-50 p-4 rounded-lg flex justify-between">
                <span className="font-medium">{item.category}</span>
                <span className="text-red-600">{formatCurrency(item.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseList;
